import { api } from '../../lib/api';
import type { IntakeState } from './types';

/**
 * Intake transport.
 *
 * ── CONTRACT ──
 *   GET  /workspaces/:workspaceId/requests/:requestId/intake        -> IntakeState
 *   POST /workspaces/:workspaceId/requests/:requestId/intake/answers -> IntakeState
 *        body: { answers: [{ questionId, selected?, text?, attachmentIds? }] }
 *   POST /workspaces/:workspaceId/requests/:requestId/intake/answer -> IntakeState
 *        body: { questionId: string, selected?: string[], text?: string }
 *        (the duplicate confirmation only — it acts the moment it is tapped)
 *   POST /workspaces/:workspaceId/requests/:requestId/intake/skip   -> IntakeState
 *   GET  /workspaces/:workspaceId/requests/:requestId/intake/stream -> SSE of IntakeState
 *
 * See the reconnect/poll logic in useIntakeState.ts for how the stream
 * degrades when it drops.
 */

function intakePath(workspaceId: string, requestId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/intake`;
}

export function fetchIntake(
  workspaceId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<IntakeState> {
  return api.get<IntakeState>(intakePath(workspaceId, requestId), signal);
}

export interface AnswerInput {
  readonly questionId: string;
  readonly selected?: readonly string[];
  /** A written answer, and also the "Something else" escape on a choice question. */
  readonly text?: string;
  /** Files answering an `evidence` question — already uploaded and registered. */
  readonly attachmentIds?: readonly string[];
}

export function answerIntake(
  workspaceId: string,
  requestId: string,
  input: AnswerInput,
): Promise<IntakeState> {
  return api.post<IntakeState>(`${intakePath(workspaceId, requestId)}/answer`, input);
}

/**
 * The whole round at once — every question the client answered, in one
 * call, which then settles the request.
 *
 * This replaced one POST per answered question. Each of those was a network
 * round trip with the card disabled behind it, and the last one to land
 * bought a full model re-analysis, so a three-question round cost three
 * requests and a 6-11 second wait the client had not asked for. Answers are
 * now held in the browser until they press the button, which is also what
 * makes tapping a chip feel instant: nothing leaves the page.
 *
 * Questions the client did not answer are simply left out, and the server
 * records them as skipped. That is why there is no per-question skip call
 * any more — not answering is the skip.
 */
export function submitIntakeAnswers(
  workspaceId: string,
  requestId: string,
  answers: readonly AnswerInput[],
): Promise<IntakeState> {
  return api.post<IntakeState>(`${intakePath(workspaceId, requestId)}/answers`, { answers });
}

/**
 * Throw the draft away and start again.
 *
 * Only possible while it is still a draft — once intake settles it is a real
 * request and removing it is Noorix's administrative path. The server checks
 * both that and that this is the author's own draft.
 */
export function discardDraft(workspaceId: string, requestId: string): Promise<{ discarded: boolean }> {
  return api.delete(`${intakePath(workspaceId, requestId)}`);
}

/**
 * "Skip and post" — stop asking and settle now with whatever is
 * understood. What stayed unanswered is recorded rather than cleared, so
 * Noorix picks the thread up instead of starting blind.
 */
export function settleIntakeNow(workspaceId: string, requestId: string): Promise<IntakeState> {
  return api.post<IntakeState>(`${intakePath(workspaceId, requestId)}/skip`, {});
}
